import type { SeoBrief, SeoBriefLinkSlot } from '../../seo-brief.schema';
import type { SeoScoreContext } from '../context';
import { questionCoveredBy, termCoverage } from '../keyword-match';
import { fail, minStatus, pass, pct, rangeStatus, warn, type SeoRule } from '../rule';
import { T } from '../thresholds';
import { normalizeFr } from '../tokenize-fr';
import { SeoRuleKey, SeoScoreGroup } from '../types';

const G = SeoScoreGroup.BRIEF;

/** Nombres « significatifs » d'un texte : ≥ 2 chiffres, ou suivis de %, €, ans, min, h, km… */
/**
 * Unités d'un REPÈRE PRATIQUE (2026.09.5) : durées, délais, fréquences, mesures donnés comme conseil
 * ou exemple (« prévenez 48 h avant », « gardez 2 cm »). Ils rendent un conseil applicable et ne
 * sont pas des affirmations à sourcer ; les prix, pourcentages, statistiques, nombres nus,
 * distances (rayon de déplacement) et anciennetés restent tracés.
 */
const PRACTICAL_UNITS = new Set([
  'minute',
  'minutes',
  'min',
  'heure',
  'heures',
  'h',
  'jour',
  'jours',
  'semaine',
  'semaines',
  'mois',
  'seance',
  'seances',
  'cm',
  'mm',
  'ml',
  'g',
  'l',
  'kg',
]);

/** Affirmations chiffrées d'un texte (repères pratiques et horaires exclus) : ce que FACT_TRACE doit tracer. */
export function claimNumbers(text: string): string[] {
  // « 14h30 », « 9 h 15 » : un horaire d'exemple, pas une affirmation.
  const withoutClock = text.replace(/\b\d{1,2}\s?h\s?\d{2}\b/gi, ' ');
  return significantNumbers(withoutClock).filter((n): boolean => !PRACTICAL_UNITS.has(n.split(' ')[1] ?? ''));
}

export function significantNumbers(text: string): string[] {
  const out = new Set<string>();
  const re =
    /(\d+(?:[.,]\d+)?)\s?(%|€|euros?|ans?|mois|minutes?|min|heures?|h\b|km|kg|g\b|cm|mm|ml|l\b|jours?|semaines?|séances?|seances?)?/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const num = m[1].replace(',', '.');
    const unit = m[2] ? normalizeFr(m[2]) : '';
    const digits = num.replace(/\D/g, '');
    if (digits.length >= 2 || unit.length > 0) out.add(num + (unit ? ` ${unit}` : ''));
  }
  return [...out];
}

export const BRIEF_RULES: SeoRule[] = [
  {
    key: SeoRuleKey.OUTLINE_COVERAGE,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const outline = ctx.brief?.outline ?? [];
      if (outline.length === 0) return null;
      const missing: string[] = [];
      let found = 0;
      for (const section of outline) {
        if (questionCoveredBy(ctx.headingStems, section.title, T.QUESTION_MATCH_RATIO)) found++;
        else missing.push(section.title);
      }
      const ratio = found / outline.length;
      return minStatus(
        ratio,
        T.OUTLINE_COVERAGE.PASS,
        T.OUTLINE_COVERAGE.WARN,
        (v): string => `${pct(v)} (${found}/${outline.length})`,
        missing,
      );
    },
  },
  {
    key: SeoRuleKey.PAA_COVERAGE,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const questions = [...(ctx.brief?.questions ?? []), ...(ctx.brief?.faq.map((f): string => f.question) ?? [])];
      if (questions.length === 0) return null;
      const missing: string[] = [];
      let found = 0;
      for (const q of questions) {
        if (questionCoveredBy(ctx.blockStems, q, T.QUESTION_MATCH_RATIO)) found++;
        else missing.push(q);
      }
      const ratio = found / questions.length;
      return minStatus(
        ratio,
        T.PAA_COVERAGE.PASS,
        T.PAA_COVERAGE.WARN,
        (v): string => `${pct(v)} (${found}/${questions.length})`,
        missing,
      );
    },
  },
  {
    key: SeoRuleKey.ENTITY_COVERAGE,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const entities = ctx.brief?.entities ?? [];
      if (entities.length === 0) return null;
      const cov = termCoverage(ctx.stems, entities);
      return minStatus(
        cov.ratio,
        T.ENTITY_COVERAGE.PASS,
        T.ENTITY_COVERAGE.WARN,
        (v): string => `${pct(v)} (${cov.found.length}/${entities.length})`,
        cov.missing,
      );
    },
  },
  {
    key: SeoRuleKey.FACT_TRACE,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      if (!ctx.brief) return null;
      const numbers = claimNumbers(ctx.bodyText);
      if (numbers.length === 0) return pass('aucune affirmation chiffrée', 'tous tracés');
      const factsText = normalizeFr(
        [
          ...ctx.brief.facts.map((f): string => f.statement),
          ...ctx.brief.outline.flatMap((s): string[] => s.facts ?? []),
          ...ctx.brief.faq.map((f): string => f.answer_hint ?? ''),
        ].join(' '),
      );
      const untraced = numbers.filter((n): boolean => {
        const value = n.split(' ')[0];
        const alt = value.replace('.', ',');
        return !factsText.includes(value) && !factsText.includes(alt);
      });
      const threshold = 'tous tracés dans le brief';
      if (untraced.length === 0) return pass(`${numbers.length} chiffres tracés`, threshold);
      // Un prix ou un pourcentage inventé bloque dès le premier (2026.09.5) : au bench du 08/10,
      // « une salle de sport tourne autour de 30 à 40 euros par mois » passait sous le seuil de 3.
      const money = untraced.filter((n): boolean => /^(%|€|euros?)$/.test(n.split(' ')[1] ?? ''));
      if (money.length > 0) {
        return fail(
          `${untraced.length} chiffre(s) non tracé(s) dont ${money.length} prix ou %`,
          threshold,
          untraced,
          true,
        );
      }
      if (untraced.length === 1) return warn(`1 chiffre non tracé`, threshold, untraced);
      return fail(
        `${untraced.length} chiffres non tracés`,
        threshold,
        untraced,
        untraced.length >= T.FACT_TRACE_BLOCK_UNTRACED,
      );
    },
  },
  {
    key: SeoRuleKey.LENGTH_VS_SERP,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const median = ctx.brief?.serp_median_word_count;
      if (!median || median <= 0) return null;
      const ratio = ctx.wordCount / median;
      return rangeStatus(
        ratio,
        T.LENGTH_VS_SERP.PASS,
        T.LENGTH_VS_SERP.WARN,
        (v): string => `${Math.round(v * 100) / 100} × la médiane (${median} mots)`,
      );
    },
  },
];

/**
 * Forme comparable d'une URL : minuscules, sans fragment ni slash final. Une
 * URL absolue garde son hôte ; on compare aussi les chemins seuls pour qu'un
 * lien relatif du brief matche un lien absolu de l'article (et inversement).
 */
export function hrefForms(href: string): { full: string; path: string } {
  const trimmed = href.trim().toLowerCase().replace(/#.*$/, '').replace(/\/+$/, '');
  const m = trimmed.match(/^[a-z][a-z0-9+.-]*:\/\/[^/?#]+(\/[^?#]*)?(\?.*)?$/);
  if (m) return { full: trimmed, path: `${m[1] ?? ''}${m[2] ?? ''}`.replace(/\/+$/, '') || '/' };
  return { full: trimmed, path: trimmed || '/' };
}

function hasLinkTo(ctx: SeoScoreContext, expected: string): boolean {
  const want = hrefForms(expected);
  return ctx.doc.links.some((l): boolean => {
    const got = hrefForms(l.href);
    return got.full === want.full || (want.path !== '/' && got.path === want.path);
  });
}

/** Liens internes exigés par le brief : liste globale + liens assignés par section, dédoublonnés. */
export function requiredBriefLinks(brief: SeoBrief): SeoBriefLinkSlot[] {
  const out = new Map<string, SeoBriefLinkSlot>();
  for (const l of [...brief.internal_links, ...brief.outline.flatMap((s): SeoBriefLinkSlot[] => s.links ?? [])]) {
    const key = hrefForms(l.url).full;
    if (!out.has(key)) out.set(key, l);
  }
  return [...out.values()];
}

BRIEF_RULES.push(
  {
    key: SeoRuleKey.REQUIRED_LINKS,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      if (!ctx.brief) return null;
      const required = requiredBriefLinks(ctx.brief);
      if (required.length === 0) return null;
      const missing = required.filter((l): boolean => !hasLinkTo(ctx, l.url));
      const threshold = `${required.length} lien(s) du brief présents`;
      if (missing.length === 0) return pass(`${required.length}/${required.length}`, threshold);
      const locations = missing.map((l): string => {
        const anchorInText = normalizeFr(ctx.bodyText).includes(normalizeFr(l.title));
        return `${l.url}${anchorInText ? ' (ancre présente en texte nu, sans lien)' : ''}`;
      });
      return fail(`${required.length - missing.length}/${required.length}`, threshold, locations);
    },
  },
  {
    key: SeoRuleKey.CTA_LINK,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const cta = ctx.brief?.cta;
      if (!cta || !cta.url || cta.placement === 'NONE') return null;
      if (hasLinkTo(ctx, cta.url)) return pass('lié', `lien vers ${cta.url}`);
      const goalInText = normalizeFr(ctx.bodyText).includes(normalizeFr(cta.goal));
      return fail('absent', `lien vers ${cta.url}`, [
        goalInText ? `« ${cta.goal} » présent en texte nu, sans lien` : cta.url,
      ]);
    },
  },
);

/** Exposé pour les tests. */
export const briefFail = fail;
